import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import i18n from "src/i18n/config";
import { getReleasedImages } from "src/services/imageService";
import { LoadingBox } from "src/ui/shared/Loading";

import { Box, CardActions, CardContent, Container, Grid, List, ListItem, Typography, useTheme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import semverService from "@services/semverService/semverService";
import { showErrorToast } from "@shared/toasts/Toasts";

import { Card } from "./CustomCard";
import { LightTooltip, useStyles } from "./ReleaseNotesDevicePage.style";
import { ReleasedImage } from "./types";

const getImage = (images: ReleasedImage[]): ReleasedImage => {
    const liveImage = images?.find((i) => i.channel === "live");
    const betaImage = images?.find((i) => i.channel === "beta");
    return liveImage || betaImage || images[0];
};

const getMajorReleaseVersion = (image: ReleasedImage) => {
    return parseInt(image.version.split(".")[0], 10) || 0;
};

const getChannelTooltip = (channel: ReleasedImage["channel"]) => {
    switch (channel) {
        case "live":
            return i18n.t("pages:releaseNotesDevicePage.tooltip.live");
        case "beta":
            return i18n.t("pages:releaseNotesDevicePage.tooltip.beta");
        case "nightly":
            return i18n.t("pages:releaseNotesDevicePage.tooltip.nightly");
        case "none":
            return i18n.t("pages:releaseNotesDevicePage.tooltip.none");
        default:
            return "";
    }
};

interface Accumulator {
    [key: string]: ReleasedImage;
}
const getLatestReleaseByMajorVersion = (images: ReleasedImage[]) => {
    const latestReleaseMap = images.reduce((acc: Accumulator, image) => {
        const majorVersion = getMajorReleaseVersion(image);

        const key = `${majorVersion}-${image.channel}`;
        if (!acc[key] || semverService.compare(image.version, acc[key].version)) {
            acc[key] = image;
        }

        return acc;
    }, {});

    return Object.values(latestReleaseMap);
};

const ReleaseNotesDevicePage = () => {
    const theme = useTheme();
    const params = useParams();
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);

    const [selectedDeviceImages, setSelectedDeviceImages] = useState<ReleasedImage[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const image = getImage(selectedDeviceImages);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const releasedImages = await getReleasedImages();

                const filteredImages = releasedImages.filter((i) => i.codename === params.device && i.channel !== "none");
                const sortedImages = getLatestReleaseByMajorVersion(filteredImages).sort((a, b) =>
                    semverService.compare(b.version, a.version),
                );
                setSelectedDeviceImages(sortedImages);
            } catch (_error) {
                showErrorToast(t("pages:releaseNotesDevicePage.toast.fetchImagesError"));
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [params.device, t]);

    if (loading) {
        return <LoadingBox className={classes.container} inverse />;
    }

    if (selectedDeviceImages.length === 0) {
        return (
            <Box className={classes.container}>
                <Typography className="text-center" variant="h5" gutterBottom>
                    {t("pages:releaseNotesDevicePage.noReleaseNotes")}
                </Typography>
            </Box>
        );
    }

    return (
        <Container className={classes.container} maxWidth={false}>
            <Grid
                container
                spacing={1}
                className={classes.centeredGrid}
                direction="row"
                justifyContent="center"
                alignItems="center"
            >
                <Grid
                    className={classes.gridItem}
                    size={{
                        sm: 12,
                        md: 6,
                    }}
                >
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography className={classes.boldTitle} variant="body1" gutterBottom>
                                {t("pages:releaseNotesDevicePage.deviceName")}
                            </Typography>
                            <Box>
                                <Typography variant="body1">{selectedDeviceImages[0].codename}</Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid
                    className={classes.gridItem}
                    size={{
                        sm: 12,
                        md: 6,
                    }}
                >
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography className={classes.boldTitle} variant="body1" gutterBottom>
                                {t("pages:releaseNotesDevicePage.latestStableAndroidVersion")}
                            </Typography>
                            <Box>
                                <Typography variant="body1">
                                    {`Android ${getMajorReleaseVersion(image)} | Release ${image.version} | `}
                                    <LightTooltip placement="top" title={getChannelTooltip(image.channel)}>
                                        <span>{image.channel}</span>
                                    </LightTooltip>
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid className={classes.gridItem} size={12}>
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography className={classes.boldTitle} variant="body1">
                                {t("pages:releaseNotesDevicePage.changeLog")}
                            </Typography>
                            <Box style={{ marginLeft: "0.8rem" }}>
                                <List style={{ listStyleType: "disc" }}>
                                    {image.changes.map((change, index) => (
                                        <ListItem key={index} style={{ display: "list-item" }} disableGutters>
                                            <Typography variant="body1">{change}</Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid className={classes.gridItem} size={12}>
                    <Card className={clsx(classes.card, classes.downloadCard)}>
                        <a href="/register" target="_blank">
                            <Box style={{ color: theme.palette.emteria.primary }}>
                                <CardContent>
                                    <Typography className={classes.boldTitle} variant="body1" gutterBottom>
                                        {t("pages:releaseNotesDevicePage.officialDownload")}
                                    </Typography>
                                    <Box>
                                        <Typography variant="body1">
                                            {t("pages:releaseNotesDevicePage.registerToDownload")}
                                        </Typography>
                                    </Box>
                                </CardContent>
                                <CardActions style={{ justifyContent: "end" }}>
                                    <i className="fa-solid fa-fw fa-download" style={{ color: theme.palette.emteria.primary }} />
                                </CardActions>
                            </Box>
                        </a>
                    </Card>
                    <Box style={{ float: "right", marginTop: theme.spacing(4) }}>
                        <a style={{ color: theme.palette.emteria.link }} href="/login" target="_blank">
                            {t("pages:releaseNotesDevicePage.loginToDownload")}
                        </a>
                    </Box>
                </Grid>
                <Grid className={classes.gridItem} size={12}>
                    <Typography className={classes.availableAndroidVersionsTitle} variant="body1" gutterBottom>
                        {t("pages:releaseNotesDevicePage.availableAndroidVersions")}
                    </Typography>
                </Grid>
                <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                    {selectedDeviceImages.map((i, index) => (
                        <Grid
                            className={classes.gridItem}
                            key={index}
                            size={{
                                xs: 12,
                                sm: 4,
                            }}
                        >
                            <Box className={classes.card}>
                                <Typography variant="body1" gutterBottom>
                                    {t("pages:releaseNotesDevicePage.androidVersion", { version: getMajorReleaseVersion(i) })}
                                </Typography>
                                <Typography variant="body1">
                                    {`${i18n.t("common:label.versionWithValue", { version: i.version })} | `}
                                    <LightTooltip placement="top" title={getChannelTooltip(i.channel)}>
                                        <span>{i.channel}</span>
                                    </LightTooltip>
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        </Container>
    );
};

export default withMetaDescription(ReleaseNotesDevicePage, {
    title: i18n.t("pages:releaseNotesDevicePage.metaTitle"),
    description: i18n.t("pages:releaseNotesDevicePage.metaDescription"),
});
